<template>
  <profile-section
    section-title="Plan Details"
  >
    <loading-bar
      v-if="loading"
      class="loading"
    />
    <template v-else>
      <!-- This should be implemented when we have the ability to resume auto renew -->
      <!-- :action-text="subscriptionReceipt.autoRenew ? 'Stop Auto Renew' : 'Resume Auto Renew'" -->
      <profile-subsection
        v-if="hasActiveSubscription"
        action-text="Stop Auto Renew"
        :disabled="activeSection && activeSection !== 'Subscription'"
        :show-action="allowCancellation"
        @inactive-action="handleAutoRenew"
      >
        <template v-slot:inactive>
          <p>{{ subscriptionReceipt.name }}</p>
          <p v-if="subscriptionReceipt.autoRenew">
            Next Charge: {{ subscriptionEndDate }}
          </p>
          <p v-else>
            Plan Ends: {{ subscriptionEndDate }}
          </p>
        </template>
      </profile-subsection>
      <!-- This should be implemented when we have the ability to resume auto renew -->
      <!-- :action-text="hasExpiredSubscription ? 'Resume Auto Renew' : 'Become a Member'" -->
      <profile-subsection v-else-if="hasPurchasedTiered">
        <template v-slot:inactive>
          <p>{{ tieredUserText }}</p>
        </template>
      </profile-subsection>
      <profile-subsection
        v-else
        action-text="Become a Member"
        :disabled="activeSection && activeSection !== 'Subscription'"
        @inactive-action="subscriptionInactiveAction"
      >
        <template v-slot:inactive>
          <p>{{ nonPaidUserText }}</p>
        </template>
      </profile-subsection>
      <profile-subsection
        v-if="card && subscriptionReceipt"
        action-text="Update Payment Info"
        :disabled="activeSection && activeSection !== 'Subscription'"
        @inactive-action="updateCreditCard"
      >
        <template v-slot:inactive>
          <p>
            **** **** **** {{ card.last4 }}
          </p>
        </template>
      </profile-subsection>
    </template>
    <subscription-modal
      @set-cards="setCards"
      @close="closeModal('subscription-info-modal')"
    />
    <cancel-subscription-modal
      :subscription-end-date="subscriptionEndDate"
      :trial="isInTrial"
      @close="closeModal('cancel-subscription-modal')"
      @endSubscription="endSubscription"
    />
  </profile-section>
</template>

<script>
import { format } from 'date-fns';

import ProfileSection from '@components/profile/section';
import ProfileSubsection from '@components/profile/subsection';
import SubscriptionModal from '@components/profile/modals/subscription';
import CancelSubscriptionModal from '@components/profile/modals/cancel-subscription';
import LoadingBar from '@components/singletons/loading-bar';

import ProfileMixin from '@/mixins/profile';
import TrialMixin from '@/mixins/trial';

export default {
  components: { CancelSubscriptionModal, LoadingBar, ProfileSection, ProfileSubsection, SubscriptionModal },
  mixins: [ProfileMixin, TrialMixin],
  props: {
    activeSection: {
      type: String,
    },
    subscriptionReceipt: {
      type: Object,
    },
    user: {
      type: Object,
    },
  },
  data() {
    return {
      card: null,
      loading: true,
      subCancellingInProgress: false,
    };
  },
  computed: {
    allowCancellation() {
      return this.subscriptionReceipt && this.subscriptionReceipt.autoRenew && this.subscriptionReceipt.paymentMethod === 'Stripe';
    },
    hasActiveSubscription() {
      if (this.subscriptionReceipt) {
        const today = new Date();
        const expirationDate = new Date(this.subscriptionReceipt.expiresAt);
        return today < expirationDate;
      }
    },
    hasExpiredSubscription() {
      if (this.subscriptionReceipt) {
        const today = new Date();
        const expirationDate = new Date(this.subscriptionReceipt.expiresAt);
        return today > expirationDate;
      }
    },
    hasPurchasedTiered() {
      const tieredPurchaseOrders = this.$store.getters['purchaseOrders/tieredPricing'].map(po => po.id);
      return this.$store.getters['purchaseReceipts/all'].some(pr => pr.active && tieredPurchaseOrders.includes(pr.purchaseOrderId));
    },
    isInTrial() {
      if (this.subscriptionReceipt) {
        return this.subscriptionReceipt.isSubscriptionTrial;
      }
    },
    nonPaidUserText() {
      if (this.trialPurchaseReceipt && !this.trialPurchaseReceiptExpired) {
        return 'Trial Member';
      } else {
        return 'Free User';
      }
    },
    subscriptionEndDate() {
      return this.subscriptionReceipt && format(new Date(this.subscriptionReceipt.expiresAt), 'MM/dd/yyyy');
    },
    tieredUserText() {
      if (this.tieredReceiptStartYear) {
        return `Member since ${this.tieredReceiptStartYear}`;
      } else {
        return 'Member';
      }
    },
    tieredReceiptStartYear() {
      if (this.hasPurchasedTiered) {
        const tieredPurchaseOrders = this.$store.getters['purchaseOrders/tieredPricing'].map(po => po.id);
        const receipt = this.$store.getters['purchaseReceipts/all'].find(pr => tieredPurchaseOrders.includes(pr.purchaseOrderId));
        return receipt?.startsAt?.getFullYear();
      }
    },
  },
  mounted() {
    this.getCards()
      .then(cards => {
        this.card = cards[0];
        this.loading = false;
      }).catch(() => {
        this.$notify({
          group: 'notifications',
          text: 'An error occurred while loading profile information. Please contact support.',
          type: 'error',
        });
      });
  },
  methods: {
    closeModal(name) {
      this.$modal.hide(name);
    },
    endSubscription() {
      this.closeModal('cancel-subscription-modal');
      this.$store.getters['session/ajax'].post('user/cancel', {
        purchaseReceiptId: this.subscriptionReceipt.id,
      }).then(resp => {
        this.$notify({
          group: 'notifications',
          text: 'Subscription cancelled successfully!',
          type: 'success',
        });
        this.$store.commit('user/set', resp.data.user);
      }).catch(e => {
        this.$notify({
          group: 'notifications',
          text: 'An error occurred while cancelling subscription. Please contact support.',
          type: 'error',
        });
      });
    },
    handleAutoRenew() {
      if (this.subscriptionReceipt && this.subscriptionReceipt.autoRenew) {
        this.$modal.show('cancel-subscription-modal');
      }
      /*
      else {
        this.resumeAutoRenew()
          .then((a) => {
            this.$notify({
              group: 'notifications',
              text: 'Subscription resumed successfully!',
              type: 'success'
            })
          }).catch(() => {
            this.$notify({
              group: 'notifications',
              text: 'An error occurred while resuming subscription. Please contact support.',
              type: 'danger'
            })
          })
      }
      */
    },
    setCards(cards) {
      this.card = cards[0];
    },
    subscriptionInactiveAction() {
      this.$store.dispatch('ui/toggleUpsellModal', { show: true, source: { type: 'Profile Screen' }});
    },
    updateCreditCard() {
      this.$analytics.events.updatedCreditCard();
      this.$modal.show('subscription-info-modal');
    },
  },
};
</script>

<style lang="scss" scoped>
  .loading {
    margin: rem-calc(50) auto;
  }
</style>
